<template>
  <v-dialog
    v-if="selected"
    :value="dialog"
    :hide-overlay="!dialog"
    @click:outside.stop="closeDialog(true)"
    @keydown.esc="closeDialog(true)"
    persistent
    max-width="599px"
    :fullscreen="is_mobile"
    :scrollable="is_mobile"
    overlay-opacity="50"
    overlay-color="#ffffff"
    content-class="default-dialog"
    >
    <v-card class="default-dialog-card">
      <v-card-title>
        <span class="headline">
          <v-icon class="toolbar-icon" v-text="lang.scheduler.icon"/>
          {{ `${lang.scheduler.title_s} #${selected.id}` }}
        </span>
        <v-spacer></v-spacer>
        <v-icon class="dialog-close-btn" @click="closeDialog(true)">mdi-close</v-icon>
      </v-card-title>
      <v-card-text class="px-1 view-info">
        <v-container
          v-bar="{ preventParentScroll: true, scrollThrottle: 30, resizeRefresh: true }"
          class="overflow-y-auto dialog-scrollbar"
          style="max-height: 350px; padding: 12px 9px 12px 0px !important;"
        >
          <div>
            <v-row>
              <v-col cols="3">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.id }}</strong></span><br />
                  #{{ selected.id }}
                </v-card-text>
              </v-col>
              <v-col cols="4">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.created }}</strong></span><br />
                  {{ formatDate(selected.created) }}
                </v-card-text>
              </v-col>
              <v-col cols="5">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.title }}</strong></span><br />
                  {{ selected.title }}
                </v-card-text>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="7">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.customer }}</strong></span><br />
                  <Customer :value="selected.customer" noPadding styled />
                </v-card-text>
              </v-col>
              <v-col cols="5">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.scheduling }}</strong></span><br />
                  <div class="scheduling-details">
                    <v-icon
                      small
                      color="green"
                    >
                      mdi-calendar-arrow-left
                    </v-icon>
                    <span class="text-subtitle-2 ml-1">{{ formatDate(selected.start) }}</span><br />
                    <v-icon
                      small
                      color="amber"
                    >
                      mdi-calendar-arrow-right
                    </v-icon>
                    <span class="text-subtitle-2 ml-1">{{ formatDate(selected.end) }}</span>
                  </div>
                </v-card-text>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.status }}</strong></span><br />
                  <v-chip
                    dark
                    :color="getStatus(selected.status).color"
                    class="status-chip"
                  >
                    <v-icon small v-text="getStatus(selected.status).icon" class="mr-2" />
                    {{ getStatus(selected.status).title }}
                  </v-chip>
                </v-card-text>
              </v-col>
              <v-col cols="6">
                <v-card-text class="pa-0">
                  <span><strong>{{ lang.scheduler.datatable.headers.action }}</strong></span><br />
                  <Actions
                    :schedule="selected"
                    @view="$emit('view', item)"
                    @edit="$emit('edit', item)"
                    hideView
                  />
                </v-card-text>
              </v-col>
            </v-row>
            <v-row class="cash-form-row">
              <v-col cols="12" class="historic">
                <v-subheader>{{ lang.orders.datatable.headers.history }}</v-subheader>
                <v-container
                  class="overflow-y-auto"
                  v-bar="{ preventParentScroll: true, scrollThrottle: 30 }"
                >
                  <v-card
                    v-if="(typeof selected.history !== 'undefined')"
                    class="mx-auto historic-inside"
                    tile
                  >
                    <v-list-item
                      v-for="(history, index) in selected.history.slice().reverse()"
                      :key="`H-${index}`"
                      class="history"
                      two-line
                    >
                      <v-list-item-icon class="history-icon">
                        <v-icon :color="getHistory(history.type).color">
                          {{ getHistory(history.type).icon }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="history-content">
                        <v-list-item-title>{{ getHistory(history.type).title }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ formatDate(history.datetime) }}
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn small icon v-on="on">
                                <v-icon small color="grey lighten-1">mdi-information</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ history.by_name }} ({{ history.by_function }})</span>
                          </v-tooltip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </v-container>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="gray" text @click="closeDialog(true)">{{ lang.items.view.close }}</v-btn>
        <v-btn
          @click="$emit('edit', true)"
          :loading="get_interface.loading"
          :disabled="get_interface.loading"
          color="amber"
          text
        >
          {{ lang.scheduler.actions.edit }}
        </v-btn>
        <v-btn
          @click="save_(selected)"
          :loading="get_interface.loading"
          :disabled="get_interface.loading"
          color="green darken-1"
          text
        >
          {{ lang.scheduler.actions.save }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Customer from '@/components/customers/templates/MiniCard'
import Actions from './list/templates/Actions'

import appdata from '@/mixins/appdata'
import utils from '@/mixins/utils'
import view from '@/mixins/defaults/view'
import scheduler from './mixins/index'

export default {
  components: { Customer, Actions },
  mixins: [ appdata, utils, view, scheduler ]
}
</script>

<style src="@/styles/inner/view.scss" lang="scss"></style>
